import React, { useState, useRef, useMemo } from 'react'
import { TouchableOpacity, View, Dimensions } from 'react-native'
import { Autocomplete, AutocompleteItem, Icon } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { productFuseSearchAtom, productsDictAtom } from '../global/AtomsProvider'
import { searchProductsByStore } from '../utils/fuseHelper'
import { getProductsList, isVisibleInNewSale } from '../utils/productListHelpers'

export default React.memo(function ProductSearch({ onSelectCallback, style=null, placeholder='productSearchPlaceholder', store='all', excludeHiddenInNewSale=false }) {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const fuse = useAtomValue(productFuseSearchAtom)
  const productsDict = useAtomValue(productsDictAtom)
  // ATL-968: getProductsList orders the catalog, so the placeholder is the first five
  // products alphabetically rather than five arbitrary ones off the front of the dict.
  // Memoized because this sorts the WHOLE catalog and the component re-renders on every
  // keystroke (onChangeText -> setValue): without it a 3,400-product org re-sorts its
  // catalog per character typed, to produce five rows that did not change.
  // ATL-1366: both the idle placeholder and the search itself read the WHOLE catalogue, so on a
  // New Sale surface they have to drop isHiddenInNewSale products — otherwise hiding an ingredient
  // leaves it one keystroke (or zero, in the placeholder's five rows) away from being sold.
  const placeholderData = useMemo(
    () => getProductsList(productsDict, store !== 'all', store === 'all' ? null : store)
      .filter((item) => !excludeHiddenInNewSale || isVisibleInNewSale(item))
      .slice(0, 5)
      .map((item) => ({ item: item })),
    [productsDict, store, excludeHiddenInNewSale]
  )
  // ATL-1366: `results === null` means "nothing searched, or cleared" and renders the DERIVED
  // placeholder rows. They used to be copied into state once at mount, which froze them for the
  // lifetime of the screen: a product hidden while the box sat idle stayed in the five rows, and
  // tapping it handed the stale, now-hidden product straight to `onSelectCallback`.
  //
  // The search rows are DERIVED for the same reason. Holding them in state froze the other half of
  // the same defect: the dropdown is open for as long as the cashier looks at it, and a product
  // hidden (or repriced) while it is open stayed offered, and `onSelectCallback` was handed the
  // snapshot taken at keystroke time — the old price, or the product that was just hidden.
  // `searched` only records whether anything has been typed since the last clear, so a query
  // deleted back to '' keeps behaving exactly as it did (empty result set, not the placeholder).
  const [searched, setSearched] = useState(false)
  const results = useMemo(() => {
    if (!searched) return null
    // No fuse limit: the shared 'all' index is filtered by store AFTER search, so a
    // pre-filter limit would cut in-store matches that rank below other stores' matches
    // (starving the dropdown to as few as 0). Filter first, then cap the display (ATL-631).
    const matches = searchProductsByStore(fuse, value, store)
    const visible = excludeHiddenInNewSale
      ? matches.filter((match) => isVisibleInNewSale(match.item))
      : matches
    return visible.slice(0, 20)
  }, [searched, fuse, value, store, excludeHiddenInNewSale])
  const data = results ?? placeholderData
  const _viewRef = useRef(null)

  const onSelect = (index) => {
    const item = data[index]
    setValue('')
    setSearched(false)
    // ATL-1366: `data` is DERIVED from live atoms now, not state that only a keystroke changed —
    // a realtime product update can shorten the row list between the render that produced the
    // dropdown and the tap that closes it. A row that is no longer there must close the box, not
    // throw "Cannot read property 'item' of undefined" in the middle of a sale.
    if (!item?.item) return
    onSelectCallback(item.item)
  }

  const onChangeText = (query) => {
    setValue(query)
    setSearched(true)
  }

  const renderItem = (item) => (
    <AutocompleteItem key={item?.item?.id} title={item?.item?.name} />
  )

  const clearInput = () => {
    setValue('')
    setSearched(false)
  }

  const renderCloseIcon = (props) => (
    <TouchableOpacity onPress={clearInput}>
      <Icon {...props} name='close'/>
    </TouchableOpacity>
  )

  return (
    <View ref={_viewRef} style={[{width: '100%', overflow: 'hidden'}, style]}>
      <Autocomplete
        placeholder={t(placeholder)}
        value={value}
        onChangeText={onChangeText}
        onSelect={onSelect}
        accessoryRight={renderCloseIcon}
        style={{width: (_viewRef.current?.offsetWidth || (Dimensions.get('window').width-40))}}
        returnKeyType='done'
      >
        {data.map(renderItem)}
      </Autocomplete>
    </View>
  )
}, function(prev, next) {
  if (prev.store !== next.store) {
    return false
  }
  if (prev.excludeHiddenInNewSale !== next.excludeHiddenInNewSale) {
    return false
  }
  // ATL-1366: without this the component keeps the callback it mounted with. `NewSale`'s
  // `addNewCartItem` is a useCallback over `dynamicDiscountOptions`, `userRole`, `store` and the
  // checkout-blocked reporter, so a frozen one applies a withdrawn default discount and reports
  // refusals through a stale emitter. (It is deliberately NOT a callback over `productsDict` any
  // more — it reads the catalogue through a ref — so catalogue updates alone no longer re-render
  // this component.) ProductGrid's own comparator already treats that callback as significant; the
  // two disagreeing is what made it unreachable. Re-rendering is cheap — the catalogue sort is
  // behind `placeholderData`'s useMemo, which is what ATL-968 was about.
  if (prev.onSelectCallback !== next.onSelectCallback) {
    return false
  }
  return true
})